import { SetterOrUpdater } from 'recoil';
import type { FlowV2_Update_Metadata } from '~/graphql/types';
import type {
  ClientFlowAction,
  ClientFlowActionIfElse,
} from '~/graphql/types.client';
import getChildActions from '~/components/page/Automation/v2/components/Builder/utils/getChildActions';
import assignFlowMetadata from './utils/assignFlowMetadata';
import deleteIfElseAction from './utils/deleteIfElseAction';
import type { Dialog } from '~/components/page/Automation/v2/state/dialogs';
import type { PathType } from '../../../IfElseCard/types';
import isMetadataRequiredAction from '~/typeguards/isMetadataRequiredAction';

const text = {
  header: 'Weet je zeker dat je deze stap wilt verwijderen?',
  description:
    'Contacten worden uit de onderliggende wacht of druppel acties verwijderd.',
};

const onDeleteActionIfElse = ({
  subjectAction,
  actions,
  setActions,
  setLastDeletedActionNextChildId,
  setDialog,
  setFlowMetadata,
  initialActions,
  initialFlowEnabled,
  options,
}: {
  subjectAction: ClientFlowActionIfElse;
  setActions: SetterOrUpdater<Array<ClientFlowAction>>;
  setLastDeletedActionNextChildId: (parentId: string | null) => void;
  actions: Array<ClientFlowAction>;
  setDialog: SetterOrUpdater<Dialog | null>;
  setFlowMetadata: SetterOrUpdater<Array<FlowV2_Update_Metadata>>;
  initialActions: Array<ClientFlowAction>;
  initialFlowEnabled: boolean;
  options?: { pathType: PathType | null };
}): void => {
  let lastDeletedActionNextChildId: string | null = null;
  let deletedChildrenIds: Array<string> = [];
  let prevActions: Array<ClientFlowAction> = [];

  setActions(actions => {
    prevActions = actions;

    const {
      actions: nextActions,
      childActionsToDelete,
      deletedActionId,
    } = deleteIfElseAction({
      actions,
      subjectAction,
      options: options || { pathType: null },
    });

    deletedChildrenIds = childActionsToDelete;
    lastDeletedActionNextChildId = deletedActionId;

    return nextActions;
  });

  const deletedActionIds = [...deletedChildrenIds, subjectAction.id];

  const onDelete = () => {
    setLastDeletedActionNextChildId(lastDeletedActionNextChildId);
    setDialog(null);
  };

  const metadataRequiredActions = getChildActions(subjectAction, actions)
    .filter(a => isMetadataRequiredAction(a) && deletedActionIds.includes(a.id))
    .map(a => a.id);

  const savedMetadataRequiredActions = metadataRequiredActions.filter(id =>
    initialActions.some(savedAction => savedAction.id === id),
  );

  if (initialFlowEnabled && savedMetadataRequiredActions.length > 0) {
    setDialog({
      type: 'dialogConfirm',
      onConfirm: () => {
        setFlowMetadata(prev =>
          assignFlowMetadata({
            prevMetadata: prev,
            deletedActionIds,
            deletedMetadataActions: savedMetadataRequiredActions,
            initialActions,
          }),
        );
        onDelete();
      },
      onCancel: () => {
        setActions(prevActions);
      },
      modalHeader: text.header,
      modalDescription: text.description,
    });

    return;
  }

  onDelete();
};

export default onDeleteActionIfElse;
